import React, { useState, useCallback, useEffect } from "react";
import { Button, message as antMessage } from "antd";
import { ThunderboltOutlined, CloseOutlined } from "@ant-design/icons";
import { useIntl } from "react-intl";

interface QuickQuestionFloatingProps {
  questions: Bot.QuickQuestion[];
  onQuestionClick?: (question: Bot.QuickQuestion) => void;
  containerRef?: React.RefObject<HTMLDivElement>;
  disabled?: boolean;
  token: string;
  defaultExpanded?: boolean; // 是否默认展开
}

const QuickQuestionFloating: React.FC<QuickQuestionFloatingProps> = ({
  questions,
  onQuestionClick,
  containerRef,
  disabled = false,
  token,
  defaultExpanded = true,
}) => {
  const intl = useIntl();
  const [isExpanded, setIsExpanded] = useState(defaultExpanded);

  // 调试信息
  console.log("🔍 QuickQuestionFloating - 组件初始化:", {
    defaultExpanded,
    isExpanded,
    questionsCount: questions.length,
  });
  const [isDragging, setIsDragging] = useState(false);

  // 监听 defaultExpanded 的变化
  useEffect(() => {
    console.log(
      "🔍 QuickQuestionFloating - defaultExpanded 变化:",
      defaultExpanded
    );
    setIsExpanded(defaultExpanded);
  }, [defaultExpanded]);
  const [dragPosition, setDragPosition] = useState({ x: 0, y: 0 });
  const [dragStart, setDragStart] = useState({ x: 0, y: 0 });
  const [isLoading, setIsLoading] = useState(false);

  // 获取事件坐标（兼容鼠标和触摸）
  const getEventCoordinates = (e: MouseEvent | TouchEvent) => {
    if ("touches" in e) {
      return { x: e.touches[0].clientX, y: e.touches[0].clientY };
    }
    return { x: e.clientX, y: e.clientY };
  };

  // 拖拽开始（鼠标）
  const handleMouseDown = useCallback((e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(true);
    setDragStart({
      x: e.clientX,
      y: e.clientY,
    });
  }, []);

  // 拖拽开始（触摸）
  const handleTouchStart = useCallback((e: React.TouchEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(true);
    setDragStart({
      x: e.touches[0].clientX,
      y: e.touches[0].clientY,
    });
  }, []);

  // 拖拽移动
  const handleMove = useCallback(
    (e: MouseEvent | TouchEvent) => {
      if (!isDragging) return;

      const { x: currentX, y: currentY } = getEventCoordinates(e);

      const deltaX = currentX - dragStart.x;
      const deltaY = currentY - dragStart.y;

      // 使用视窗尺寸进行边界检测
      const panelSize = isExpanded ? 280 : 48; // 更新尺寸

      const newX = Math.max(
        0,
        Math.min(window.innerWidth - panelSize, dragPosition.x + deltaX)
      );
      const newY = Math.max(
        0,
        Math.min(window.innerHeight - panelSize, dragPosition.y + deltaY)
      );

      setDragPosition({ x: newX, y: newY });

      setDragStart({
        x: currentX,
        y: currentY,
      });
    },
    [isDragging, dragStart, dragPosition, isExpanded]
  );

  // 拖拽结束
  const handleEnd = useCallback(() => {
    setIsDragging(false);
  }, []);

  // 添加全局事件监听
  useEffect(() => {
    if (isDragging) {
      document.addEventListener("mousemove", handleMove);
      document.addEventListener("mouseup", handleEnd);
      document.addEventListener("touchmove", handleMove, { passive: false });
      document.addEventListener("touchend", handleEnd);

      document.body.style.userSelect = "none";

      return () => {
        document.removeEventListener("mousemove", handleMove);
        document.removeEventListener("mouseup", handleEnd);
        document.removeEventListener("touchmove", handleMove);
        document.removeEventListener("touchend", handleEnd);
        document.body.style.userSelect = "";
      };
    }
  }, [isDragging, handleMove, handleEnd]);

  // 初始化位置（固定在提交按钮上方，与关闭按钮错开）
  useEffect(() => {
    const initializePosition = () => {
      if (dragPosition.x === 0 && dragPosition.y === 0) {
        const buttonSize = 48; // 更新按钮尺寸
        const panelWidth = 280; // 更新面板宽度
        const padding = 20;

        // 使用视窗尺寸，固定在提交按钮上方，与关闭按钮错开
        // 位置：右下角左侧，避免与关闭按钮重叠
        let initialX = window.innerWidth - buttonSize - padding - 60; // 向左偏移，避免与关闭按钮重叠
        let initialY = window.innerHeight - buttonSize - padding - 80; // 在提交按钮上方，与关闭按钮同高度

        // 如果展开面板会超出右边界，调整位置
        if (initialX + panelWidth > window.innerWidth - padding) {
          initialX = window.innerWidth - panelWidth - padding;
        }

        // 确保不超出视窗边界
        initialX = Math.max(
          padding,
          Math.min(initialX, window.innerWidth - buttonSize - padding)
        );
        initialY = Math.max(
          padding,
          Math.min(initialY, window.innerHeight - buttonSize - padding)
        );

        setDragPosition({
          x: initialX,
          y: initialY,
        });
      }
    };

    const timer = setTimeout(initializePosition, 100);
    return () => clearTimeout(timer);
  }, [dragPosition.x, dragPosition.y]);

  // 监听窗口大小变化，重新调整位置
  useEffect(() => {
    const handleResize = () => {
      const buttonSize = 48;
      const panelWidth = 280;
      const padding = 20;

      // 重新计算位置，确保按钮在视窗内，保持在提交按钮上方
      let newX = Math.max(
        padding,
        Math.min(window.innerWidth - buttonSize - padding - 60, dragPosition.x)
      );
      let newY = Math.max(
        padding,
        Math.min(window.innerHeight - buttonSize - padding - 80, dragPosition.y)
      );

      // 如果展开面板会超出右边界，调整位置
      if (isExpanded && newX + panelWidth > window.innerWidth - padding) {
        newX = window.innerWidth - panelWidth - padding;
      }

      setDragPosition({ x: newX, y: newY });
    };

    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, [dragPosition.x, dragPosition.y, isExpanded]);

  // 处理快捷问题点击
  const handleQuestionClick = useCallback(
    async (question: Bot.QuickQuestion) => {
      if (isLoading || disabled) return;

      try {
        setIsLoading(true);

        // 调用父组件的回调，让父组件处理快捷回复逻辑
        if (onQuestionClick) {
          await onQuestionClick(question);
        }

        // 自动收起面板
        setIsExpanded(false);
      } catch (error) {
        console.error("快捷问题处理失败:", error);
        antMessage.error(intl.formatMessage({ id: "quick.question.error" }));
      } finally {
        setIsLoading(false);
      }
    },
    [isLoading, disabled, onQuestionClick, intl]
  );

  // 切换展开/收起状态
  const toggleExpanded = useCallback(() => {
    if (disabled || isLoading) return;
    setIsExpanded(!isExpanded);
  }, [isExpanded, disabled, isLoading]);

  // 如果没有快捷问题，不显示组件
  if (!questions || questions.length === 0) {
    return null;
  }

  return (
    <div
      style={{
        position: "fixed",
        top: `${dragPosition.y}px`,
        left: `${dragPosition.x}px`,
        zIndex: 200,
        transition: isDragging
          ? "none"
          : "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
        transform: isExpanded ? "scale(1)" : "scale(1)",
        opacity: disabled ? 0.5 : 1,
      }}
    >
      <style>
        {`
          @keyframes slideIn {
            from {
              opacity: 0;
              transform: scale(0.9) translateY(-10px);
            }
            to {
              opacity: 1;
              transform: scale(1) translateY(0);
            }
          }
          
          @keyframes fadeIn {
            from {
              opacity: 0;
              transform: scale(0.8);
            }
            to {
              opacity: 1;
              transform: scale(1);
            }
          }
        `}
      </style>
      {/* 收起状态 - 图标按钮 */}
      {!isExpanded && (
        <Button
          type="default"
          shape="circle"
          icon={<ThunderboltOutlined />}
          onClick={toggleExpanded}
          onMouseDown={handleMouseDown}
          onTouchStart={handleTouchStart}
          disabled={disabled || isLoading}
          title={intl.formatMessage({ id: "quick.question.title" })}
          style={{
            width: "48px",
            height: "48px",
            borderRadius: "50%",
            border: isDragging
              ? "2px solid #1890ff"
              : "1px solid rgba(24, 144, 255, 0.3)",
            color: isDragging ? "#0050b3" : "#1890ff",
            background: isDragging
              ? "rgba(24, 144, 255, 0.15)"
              : "rgba(255, 255, 255, 0.98)",
            backdropFilter: "blur(12px)",
            fontSize: "18px",
            fontWeight: 600,
            boxShadow: isDragging
              ? "0 8px 24px rgba(24, 144, 255, 0.3), 0 4px 12px rgba(24, 144, 255, 0.2)"
              : "0 6px 20px rgba(24, 144, 255, 0.15), 0 3px 10px rgba(24, 144, 255, 0.1)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            cursor: isDragging
              ? "grabbing"
              : disabled
              ? "not-allowed"
              : "pointer",
            transform: isDragging ? "scale(1.05)" : "scale(1)",
            userSelect: "none",
            transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
            animation: "fadeIn 0.3s ease-out",
          }}
          onMouseEnter={(e) => {
            if (disabled || isDragging) return;
            const target = e.target as HTMLElement;
            target.style.background = "rgba(24, 144, 255, 0.12)";
            target.style.color = "#0050b3";
            target.style.transform = "scale(1.1)";
            target.style.borderColor = "#1890ff";
            target.style.boxShadow =
              "0 8px 24px rgba(24, 144, 255, 0.25), 0 4px 12px rgba(24, 144, 255, 0.15)";
          }}
          onMouseLeave={(e) => {
            if (disabled || isDragging) return;
            const target = e.target as HTMLElement;
            target.style.background = "rgba(255, 255, 255, 0.98)";
            target.style.color = "#1890ff";
            target.style.transform = "scale(1)";
            target.style.borderColor = "rgba(24, 144, 255, 0.3)";
            target.style.boxShadow =
              "0 6px 20px rgba(24, 144, 255, 0.15), 0 3px 10px rgba(24, 144, 255, 0.1)";
          }}
        />
      )}

      {/* 展开状态 - 问题面板 */}
      {isExpanded && (
        <div
          style={{
            minWidth: "200px", // 减小最小宽度
            maxWidth: "280px", // 减小最大宽度
            background: "rgba(255, 255, 255, 0.98)",
            backdropFilter: "blur(12px)",
            borderRadius: "16px",
            border: "1px solid rgba(24, 144, 255, 0.1)",
            boxShadow:
              "0 12px 32px rgba(0, 0, 0, 0.15), 0 6px 16px rgba(24, 144, 255, 0.1)",
            padding: "16px", // 减小面板内边距
            userSelect: "none",
            animation: "slideIn 0.3s ease-out",
          }}
          onMouseDown={handleMouseDown}
          onTouchStart={handleTouchStart}
        >
          {/* 面板头部 */}
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              marginBottom: "12px", // 减小头部底部间距
              paddingBottom: "12px",
              borderBottom: "1px solid rgba(24, 144, 255, 0.1)",
            }}
          >
            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: "8px",
              }}
            >
              <ThunderboltOutlined
                style={{
                  fontSize: "16px",
                  color: "#1890ff",
                }}
              />
              <span
                style={{
                  fontSize: "14px", // 减小标题字体大小
                  fontWeight: 600,
                  color: "#262626",
                }}
              >
                {intl.formatMessage({ id: "quick.question.title" })}
              </span>
            </div>
            <Button
              type="text"
              size="small"
              icon={<CloseOutlined />}
              onClick={toggleExpanded}
              style={{
                padding: "6px",
                minWidth: "auto",
                color: "#8c8c8c",
                borderRadius: "6px",
                transition: "all 0.2s ease",
              }}
              onMouseEnter={(e) => {
                const target = e.target as HTMLElement;
                target.style.background = "rgba(24, 144, 255, 0.1)";
                target.style.color = "#1890ff";
              }}
              onMouseLeave={(e) => {
                const target = e.target as HTMLElement;
                target.style.background = "transparent";
                target.style.color = "#8c8c8c";
              }}
            />
          </div>

          {/* 问题列表 */}
          <div
            style={{
              display: "flex",
              flexDirection: "column",
              gap: "8px", // 减小问题之间的间距
            }}
          >
            {questions.map((question) => (
              <Button
                key={question.id}
                type="text"
                size="small"
                loading={isLoading}
                disabled={isLoading}
                onClick={() => handleQuestionClick(question)}
                style={{
                  textAlign: "left",
                  padding: "10px 14px", // 减小问题按钮内边距
                  borderRadius: "10px",
                  border: "1px solid rgba(24, 144, 255, 0.1)",
                  background: "rgba(24, 144, 255, 0.02)",
                  color: "#262626",
                  fontSize: "13px", // 减小字体大小
                  lineHeight: "1.5",
                  height: "auto",
                  whiteSpace: "normal",
                  wordBreak: "break-word",
                  transition: "all 0.2s ease",
                  fontWeight: 500,
                }}
                onMouseEnter={(e) => {
                  if (isLoading) return;
                  const target = e.target as HTMLElement;
                  target.style.background = "rgba(24, 144, 255, 0.08)";
                  target.style.borderColor = "rgba(24, 144, 255, 0.3)";
                  target.style.transform = "translateY(-1px)";
                  target.style.boxShadow =
                    "0 4px 12px rgba(24, 144, 255, 0.15)";
                }}
                onMouseLeave={(e) => {
                  if (isLoading) return;
                  const target = e.target as HTMLElement;
                  target.style.background = "rgba(24, 144, 255, 0.02)";
                  target.style.borderColor = "rgba(24, 144, 255, 0.1)";
                  target.style.transform = "translateY(0)";
                  target.style.boxShadow = "none";
                }}
              >
                {question.question}
              </Button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default QuickQuestionFloating;
